import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Heart, Check, Eye, Pencil, Trash2, Music, Headphones, Download } from 'lucide-react';
import { useAppTheme } from '@/utils/theme-context';
import { CreatorLabel } from '@/components/CreatorLabel';
import { CreatorAvatar } from '@/components/CreatorAvatar';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';
import {
  WallpapersResponse,
  subscribeManyWallpapersViews,
  WallpapersViewsResponse,
  updateWallpapers,
  deleteWallpapers,
} from '@/lib/collections/wallpapers';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { SKR_MIN_PRICE_RAW } from '@/lib/constants';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogPortal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SkrTokenIcon } from '@/components/SkrTokenIcon';
import { useLongPress } from '@/hooks/use-long-press';
import { useIsAdmin } from '@/hooks/use-is-admin';
import { useWallpaperDownload } from '@/hooks/use-wallpaper-download';
import { haptic } from '@/utils/haptic';
import { useWallpaperCategoriesContext } from '@/contexts/WallpaperCategoriesContext';
import { useImageLoaded } from '@/hooks/use-image-loaded';

const RAW_PER_SKR = 1_000_000;
const MIN_PRICE_RAW = Number(SKR_MIN_PRICE_RAW);
function getCardImageUrl(wallpaper: WallpapersResponse): string {
  const baseUrl = wallpaper.imageUrl;
  if (!baseUrl) return '';
  const stripped = baseUrl.split('?')[0];
  const isUnsplash = stripped.includes('unsplash.com') || stripped.includes('images.unsplash');
  if (!isUnsplash) return baseUrl;
  return `${stripped}?w=800&q=100&auto=format&fit=crop`;
}

function isLiveWallpaper(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'live-wallpaper') return true;
  if (stored && stored !== 'live-wallpaper') return false;
  // Legacy fallback: detect from URL extension
  const url = (wallpaper.imageUrl || '').toLowerCase().split('?')[0];
  return /\.(mp4|webm|mov|m4v)$/.test(url);
}

function isRingtone(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  return stored === 'ringtone';
}

function formatRawPrice(rawPrice?: number | null): string {
  return ((rawPrice ?? MIN_PRICE_RAW) / RAW_PER_SKR).toString();
}

function parseSkrToRaw(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+(?:\.\d{0,6})?$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * RAW_PER_SKR);
}

interface WallpaperCardProps {
  wallpaper: WallpapersResponse;
  isFavorited: boolean;
  isPurchased: boolean;
  onClick: () => void;
  onToggleFavorite: () => void;
  index: number;
  isTrending?: boolean;
  gridColumns?: number;
  minimal?: boolean;
}

export const WallpaperCard: React.FC<WallpaperCardProps> = memo(function WallpaperCard({
  wallpaper,
  isFavorited,
  isPurchased,
  onClick,
  onToggleFavorite,
  isTrending = false,
  gridColumns = 3,
  minimal = false,
}) {
  const isAdmin = useIsAdmin();
  const { downloadWallpaper } = useWallpaperDownload();
  const { tokens } = useAppTheme();

  // Admin-managed category lists — read from shared context (single subscription for the whole app)
  const { wallpaperCats, liveCats, ringtoneCats } = useWallpaperCategoriesContext();

  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Edit form fields
  const [titleInput, setTitleInput] = useState(wallpaper.title ?? '');
  const [categoryInput, setCategoryInput] = useState(wallpaper.category ?? '');
  const [priceInput, setPriceInput] = useState(() => formatRawPrice(wallpaper.price));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const suppressClickRef = useRef(false);

  const imageUrl = getCardImageUrl(wallpaper);
  const liveWallpaper = isLiveWallpaper(wallpaper);
  const ringtone = isRingtone(wallpaper);

  // Shared loaded-state: owns the cached-image fast path for the <img> tag
  // below. Video/background-image branches also feed into it via onLoad().
  const { ref: imgRef, loaded: imageLoaded, onLoad: markImageLoaded } = useImageLoaded(imageUrl);

  // Reset edit fields whenever the dialog opens
  useEffect(() => {
    if (editDialogOpen) {
      setTitleInput(wallpaper.title ?? '');
      setCategoryInput(wallpaper.category ?? '');
      setPriceInput(formatRawPrice(wallpaper.price));
    }
  }, [editDialogOpen, wallpaper.title, wallpaper.category, wallpaper.price]);

  // Preload background image once per imageUrl — stable effect, no new Image() on every render
  useEffect(() => {
    if (!imageUrl || imageLoaded) return;
    const img = new Image();
    img.onload = () => markImageLoaded();
    img.onerror = () => markImageLoaded(); // don't leave the placeholder stuck forever on failure
    img.src = imageUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, imageLoaded, markImageLoaded]);

  const { data: views } = useRealtimeData<WallpapersViewsResponse[]>(
    subscribeManyWallpapersViews,
    isTrending && !!wallpaper.id,
    wallpaper.id,
  );
  const viewCount = views?.length ?? 0;
  const shouldShowTopBadges = gridColumns <= 3;
  const createdAtMs = (wallpaper.createdAt ?? 0) * 1000;
  const isNewWallpaper = createdAtMs > 0 && Date.now() - createdAtMs <= 7 * 24 * 60 * 60 * 1000;
  const showNewBadge = !minimal && shouldShowTopBadges && isNewWallpaper;
  const showPriceBadge = !minimal && !isPurchased && (shouldShowTopBadges || ringtone);
  const showTrendingViewCount = !minimal && isTrending && viewCount > 0;

  const priceError = useMemo(() => {
    const raw = parseSkrToRaw(priceInput);
    if (raw === null) return 'Enter a valid SKR amount with up to 6 decimals.';
    if (raw < MIN_PRICE_RAW) return 'Minimum price is 1 SKR.';
    return null;
  }, [priceInput]);

  const titleError = titleInput.trim().length === 0 ? 'Title cannot be empty.' : null;

  // Long-press hook — admin only, supports both mouse and touch
  const lp = useLongPress({
    delay: 550,
    enabled: isAdmin,
    onLongPress: (x, y) => {
      haptic.medium();
      suppressClickRef.current = true;
      setMenuPos({ x, y });
    },
  });

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current || lp.triggered()) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
      lp.clearTriggered();
      return;
    }
    onClick();
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isAdmin) return;
    haptic.medium();
    suppressClickRef.current = true;
    setMenuPos({ x: event.clientX, y: event.clientY });
  };

  const handleOpenEditDialog = () => {
    setMenuPos(null);
    setEditDialogOpen(true);
  };

  const handleMenuDownload = () => {
    setMenuPos(null);
    downloadWallpaper(wallpaper);
  };

  const handleOpenDeleteDialog = () => {
    setMenuPos(null);
    setDeleteDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (titleError) {
      notify.error(titleError);
      return;
    }
    const nextRaw = parseSkrToRaw(priceInput);
    if (nextRaw === null || nextRaw < MIN_PRICE_RAW) {
      notify.error(priceError ?? 'Invalid price');
      return;
    }
    setIsSaving(true);
    const ok = await updateWallpapers(wallpaper.id, {
      title: titleInput.trim(),
      category: categoryInput || undefined,
      price: nextRaw,
    });
    setIsSaving(false);
    if (ok) {
      haptic.success();
      setEditDialogOpen(false);
      return;
    }
    haptic.error();
    notify.error('Failed to update wallpaper — check permissions');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const ok = await deleteWallpapers(wallpaper.id);
    setIsDeleting(false);
    if (ok) {
      haptic.success();
      setDeleteDialogOpen(false);
      setMenuPos(null);
      return;
    }
    haptic.error();
    notify.error('Failed to delete wallpaper');
  };

  return (
    <>
      <motion.div
        id={`wallpaper-${wallpaper.id}`}
        onClick={handleCardClick}
        onContextMenu={isAdmin ? handleContextMenu : (e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={isAdmin ? lp.onMouseDown : undefined}
        onMouseUp={isAdmin ? lp.onMouseUp : undefined}
        onMouseMove={isAdmin ? lp.onMouseMove : undefined}
        onMouseLeave={isAdmin ? lp.onMouseLeave : undefined}
        onTouchStart={isAdmin ? lp.onTouchStart : undefined}
        onTouchEnd={isAdmin ? lp.onTouchEnd : undefined}
        onTouchCancel={isAdmin ? lp.onTouchCancel : undefined}
        onTouchMove={isAdmin ? lp.onTouchMove : undefined}
        className="wallpaper-protected"
        whileHover={{
          scale: 1.03,
          y: -6,
        }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        style={{
          borderRadius: 14,
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative',
          aspectRatio: '9/14',
          background: 'transparent',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation',
          willChange: 'transform',
        } as React.CSSProperties}
      >
        {!imageLoaded && !ringtone && (
          <div style={{
            position: 'absolute', inset: 0,
            // Static neutral fill — no shimmer/pulse loop, just a stable surface
            // while the real image fades in underneath.
            background: `linear-gradient(135deg, ${tokens.shimmerFrom}, ${tokens.shimmerTo})`,
          }} />
        )}

        {/* Ringtone card — music-themed visual, orange palette */}
        {ringtone && (
          <>
            {/* Dark warm background */}
            <div style={{
              position: 'absolute', inset: 0,
              background: '#1A1025',
            }} />
            {/* Ambient orange glow */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(251,146,60,0.18) 0%, transparent 70%)',
              animation: 'ringtoneGlow 3s ease-in-out infinite alternate',
            }} />
            {/* Waveform bars */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 3,
              paddingBottom: 32,
            }}>
              {[0.4, 0.7, 1.0, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 1.0, 0.6, 0.85, 0.45].map((h, i) => (
                <div
                  key={i}
                  style={{
                    width: 3,
                    borderRadius: 2,
                    background: `rgba(251,146,60,${0.25 + h * 0.5})`,
                    height: `${h * 52}px`,
                    animation: `waveBar${(i % 4) + 1} ${0.8 + (i % 3) * 0.2}s ease-in-out infinite alternate`,
                    animationDelay: `${i * 0.07}s`,
                  }}
                />
              ))}
            </div>
            {/* Music icon with orange pulse ring */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              paddingBottom: 24,
            }}>
              <div style={{
                width: 72, height: 72,
                borderRadius: '50%',
                background: 'rgba(0,229,204,0.12)',
                border: '1px solid rgba(0,229,204,0.28)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'ringtonePulse 2.4s ease-in-out infinite',
                position: 'relative',
              }}>
                {/* Outer ring pulse */}
                <div style={{
                  position: 'absolute', inset: -8,
                  borderRadius: '50%',
                  border: '1px solid rgba(0,229,204,0.13)',
                  animation: 'ringtonePulse 2.4s ease-in-out infinite',
                  animationDelay: '0.4s',
                }} />
                {isPurchased ? (
                  <Headphones size={32} color="#00E5CC" strokeWidth={1.5} />
                ) : (
                  <Music size={32} color="#00E5CC" strokeWidth={1.5} />
                )}
              </div>
            </div>
          </>
        )}

        {!ringtone && imageUrl && (
          liveWallpaper ? (
            isPurchased ? (
              <video
                src={imageUrl}
                autoPlay
                loop
                muted
                playsInline
                onCanPlay={() => markImageLoaded()}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                } as React.CSSProperties}
              />
            ) : (
              <>
                <video
                  src={imageUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onCanPlay={() => markImageLoaded()}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  } as React.CSSProperties}
                />
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  } as React.CSSProperties}
                />
              </>
            )
          ) : (
            isPurchased ? (
              <img
                ref={imgRef}
                src={imageUrl}
                alt={wallpaper.title}
                loading="lazy"
                decoding="async"
                onLoad={markImageLoaded}
                onError={markImageLoaded}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                className="protected-image"
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  imageRendering: 'auto',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.22s ease-out',
                  WebkitUserDrag: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  pointerEvents: 'none',
                } as React.CSSProperties}
              />
            ) : (
              <>
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    pointerEvents: 'none',
                    opacity: imageLoaded ? 1 : 0,
                    transition: 'opacity 0.22s ease-out',
                  }}
                />
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  } as React.CSSProperties}
                />
              </>
            )
          )
        )}

        {showNewBadge && (
          <div style={{
            position: 'absolute', top: 8, left: 8,
            display: 'flex', alignItems: 'center',
            padding: '3px 8px',
            borderRadius: 999,
            background: 'linear-gradient(135deg, #42a5f5, #1e88e5)',
            border: '1px solid rgba(255,255,255,0.3)',
            fontSize: 10, fontWeight: 700,
            color: '#ffffff',
            fontFamily: "'DM Mono', monospace",
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(33,150,243,0.4)',
            zIndex: 3,
          }}>
            NEW
          </div>
        )}

        {showPriceBadge && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 999,
            background: 'rgba(139, 92, 246, 0.6)',
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(196, 181, 253, 0.45)',
            fontSize: 10, fontWeight: 500,
            color: '#ffffff',
            fontFamily: "'DM Mono', monospace",
            zIndex: 2,
          }}>
            <SkrTokenIcon size={12} />
            {formatRawPrice(wallpaper.price)}
          </div>
        )}

        {showTrendingViewCount && (
          <div style={{
            position: 'absolute', top: showNewBadge ? 36 : 8, left: 8,
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '2px 7px', borderRadius: 10,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.1)',
            fontSize: 10, fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
            fontFamily: "'DM Mono', monospace",
            zIndex: 2,
          }}>
            <Eye size={9} color="rgba(255,255,255,0.55)" />
            {viewCount}
          </div>
        )}

        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '24px 10px 10px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 6,
          zIndex: 2,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{
              fontSize: 14, fontWeight: 700, color: '#ffffff',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '0.01em',
            }}>
              {wallpaper.title}
            </span>
            {!minimal && wallpaper.uploadedBy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <CreatorAvatar walletAddress={wallpaper.uploadedBy} size={16} />
                <CreatorLabel walletAddress={wallpaper.uploadedBy} />
              </div>
            )}
            {minimal && wallpaper.category && (
              <span style={{
                fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.55)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '0.01em',
              }}>
                {wallpaper.category}
              </span>
            )}
          </div>
          {!minimal && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
              {isPurchased && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#22c55e',
                  flexShrink: 0,
                }}>
                  <Check size={11} color="#fff" strokeWidth={2.5} />
                </div>
              )}
              <div
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); haptic.light(); onToggleFavorite(); }}
                onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                onTouchStart={(e: React.TouchEvent) => e.stopPropagation()}
                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2 }}
              >
                <Heart
                  size={16}
                  fill={isFavorited ? 'hsl(262, 83%, 66%)' : 'none'}
                  color={isFavorited ? 'hsl(262, 83%, 66%)' : 'rgba(255,255,255,0.7)'}
                />
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes ringtonePulse {
            0% { opacity: 0.7; transform: scale(1); }
            100% { opacity: 1; transform: scale(1.06); }
          }
          @keyframes ringtoneGlow {
            0% { opacity: 0.6; }
            100% { opacity: 1; }
          }
          @keyframes waveBar1 {
            0% { transform: scaleY(0.5); } 100% { transform: scaleY(1); }
          }
          @keyframes waveBar2 {
            0% { transform: scaleY(0.7); } 100% { transform: scaleY(0.4); }
          }
          @keyframes waveBar3 {
            0% { transform: scaleY(1); } 100% { transform: scaleY(0.6); }
          }
          @keyframes waveBar4 {
            0% { transform: scaleY(0.4); } 100% { transform: scaleY(0.9); }
          }
        `}</style>
      </motion.div>

      {/* ── Admin context menu popup — rendered via portal in document.body ─────
          This bypasses any transform stacking context from framer-motion wrappers
          or position:fixed modal containers, preventing the "freeze" bug where
          the backdrop div at z:3000 got trapped inside a transformed ancestor. */}
      {isAdmin && menuPos && createPortal(
        <>
          {/* Full-screen dismiss backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10002 }}
            onClick={() => {
              setMenuPos(null);
              suppressClickRef.current = false;
              lp.clearTriggered();
            }}
          />
          {/* Context menu card */}
          <div
            style={{
              position: 'fixed',
              left: Math.min(menuPos.x, window.innerWidth - 168),
              top: Math.min(menuPos.y + 10, window.innerHeight - 110),
              zIndex: 10003,
              background: tokens.bgAdminMenu,
              border: `1px solid ${tokens.accent}55`,
              borderRadius: 14,
              padding: 6,
              minWidth: 152,
              boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px ${tokens.accentMuted}`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              animation: 'wpMenuIn 0.15s ease',
            }}
          >
            <style>{`
              @keyframes wpMenuIn {
                from { opacity: 0; transform: scale(0.9) translateY(-4px); }
                to   { opacity: 1; transform: scale(1)   translateY(0); }
              }
            `}</style>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '8px 11px', borderRadius: 9, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: tokens.textPrimary, fontSize: 13, fontWeight: 600,
                fontFamily: "'Syne', sans-serif", textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = tokens.accentMuted)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Pencil size={14} color={tokens.accent} />
              Edit
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleMenuDownload(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '8px 11px', borderRadius: 9, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: tokens.textPrimary, fontSize: 13, fontWeight: 600,
                fontFamily: "'Syne', sans-serif", textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = tokens.accentMuted)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Download size={14} color={tokens.accent} />
              Download
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '8px 11px', borderRadius: 9, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'rgba(248,113,113,0.9)', fontSize: 13, fontWeight: 600,
                fontFamily: "'Syne', sans-serif", textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.12)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Trash2 size={14} color="rgba(248,113,113,0.85)" />
              Delete
            </button>
          </div>
        </>,
        document.body,
      )}

      {/* ── Admin Edit Dialog: title, category, price ── */}
      <Dialog open={editDialogOpen} onOpenChange={(v) => { if (!v) setEditDialogOpen(false); }}>
        <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10000] bg-black data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-[#11111b] border-white/10 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[440px] text-white"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-['Syne',sans-serif] text-lg">
              Edit {ringtone ? 'ringtone' : liveWallpaper ? 'live wallpaper' : 'wallpaper'}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Admin-only. Changes apply immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {/* Title */}
            <div className="grid gap-1.5">
              <label className="text-xs uppercase tracking-[0.18em] text-white/45">Title</label>
              <Input
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder="Wallpaper title"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/25"
              />
              {titleError && <p className="text-xs text-red-300">{titleError}</p>}
            </div>
            {/* Category — dropdown only, sourced from admin-managed lists */}
            {(() => {
              const adminCats = ringtone ? ringtoneCats : liveWallpaper ? liveCats : wallpaperCats;
              const isKnown = adminCats.includes(categoryInput);
              const selectVal = isKnown ? categoryInput : (categoryInput ? '__orphan__' : '__none__');
              return (
                <div className="grid gap-1.5">
                  <label className="text-xs uppercase tracking-[0.18em] text-white/45">Category</label>
                  <Select
                    value={selectVal}
                    onValueChange={(val) => {
                      if (val === '__orphan__') return;
                      setCategoryInput(val === '__none__' ? '' : val);
                    }}
                  >
                    <SelectTrigger className="border-white/10 bg-white/5 text-white">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#1a1a2e] text-white">
                      <SelectItem value="__none__" className="text-white/45 focus:text-white">— None —</SelectItem>
                      {adminCats.map((cat) => (
                        <SelectItem key={cat} value={cat} className="text-white focus:text-white">{cat}</SelectItem>
                      ))}
                      {categoryInput && !isKnown && (
                        <SelectItem value="__orphan__" className="text-white/45 focus:text-white/45">
                          {categoryInput} (current)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            {/* Price */}
            <div className="grid gap-1.5">
              <label className="text-xs uppercase tracking-[0.18em] text-white/45">Price (SKR)</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                  <SkrTokenIcon size={14} />
                </span>
                <Input
                  inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="1.0"
                  className="border-white/10 bg-white/5 pl-8 text-white placeholder:text-white/25"
                />
              </div>
              <p className={`text-xs ${priceError ? 'text-red-300' : 'text-white/40'}`}>
                {priceError ?? 'Minimum price: 1 SKR.'}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white">
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveEdit} disabled={!!priceError || !!titleError || isSaving} className="bg-violet-500 text-white hover:bg-violet-400">
              {isSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <span className="h-4 w-4 text-white/60 text-sm">✕</span>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10000] bg-black data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-[#11111b] border-red-500/20 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[420px] text-white"
        >
          <DialogHeader>
            <DialogTitle className="font-['Syne',sans-serif] text-lg text-red-100">Delete wallpaper?</DialogTitle>
            <DialogDescription className="text-white/55">
              Are you sure? Users who purchased this wallpaper will keep it, but it will be removed from the store and favorites.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white">
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Wallpaper'}
            </Button>
          </DialogFooter>
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <span className="h-4 w-4 text-white/60 text-sm">✕</span>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  );
});

WallpaperCard.displayName = 'WallpaperCard';
